import { Children, cloneElement, isValidElement, ReactNode, useRef } from "react";
import classNames from "classnames";
import { useInView } from "framer-motion";

import useVisibilityTracker from "@utils/hooks/useVisibilityTracker";

import SectionTitle from "@molecules/section-title/SectionTitle";

import * as styles from "@organisms/company-values/CompanyValues.module.scss";

interface CompanyValuesProps {
  title: ReactNode;
  subtitle: string;
  description?: ReactNode;
  children: ReactNode;
  variantName?: string;
  adoptionClassName?: string;
  isLoading?: boolean;
}

const CompanyValues = ({
  title,
  subtitle,
  description,
  children,
  variantName,
  adoptionClassName,
}: CompanyValuesProps): JSX.Element => {
  const componentName = "or-company-values";

  const { ref } = useVisibilityTracker(variantName, {
    threshold: 0.5,
  });

  const headerRef = useRef(null);

  const isHeaderVisible = useInView(headerRef, {
    amount: 0.8,
    once: true,
    margin: "0px 0px 300px 0px",
  });

  const childrenWithProps = Children.map(children, (child) => {
    if (isValidElement(child)) {
      const childClassName = classNames(
        styles.companyValuesBodyWrapper,
        styles.companyValuesBodyWrapperNotVisited,
        {
          [styles.companyValuesBodyWrapperAnimation]: isHeaderVisible,
        }
      );

      return cloneElement(child, {
        className: childClassName,
      } as React.HTMLAttributes<HTMLElement>);
    }

    return child;
  });

  return (
    <section
      ref={ref}
      className={classNames(componentName, styles.companyValues, adoptionClassName, {
        [styles[variantName as string]]: variantName,
      })}
    >
      <div ref={headerRef} className={styles.companyValuesHeader}>
        <SectionTitle title={title} subtitle={subtitle} adoptionClassName={styles.sectionTitle} />
        {description && <p className={styles.companyValuesDescription}>{description}</p>}
      </div>
      {childrenWithProps}
    </section>
  );
};

export default CompanyValues;
